<template>
  <div ref="youtubeContainer" class="iframe"></div>
</template>

<script>
  export default {
    name: 'YoutubeFrame',
    props: ['videoUrl'],
    data() {
      return {
        currentVideoId: null,
        videoDuration: null,
        checkInterval: null,
        advanceTimeout: null,
        player: null
      }
    },
    computed: {
      iframeSrc() {
        if (!this.videoUrl) {
          console.log('No videoUrl provided to iframe')
          return ''
        }

        const videoId = this.extractVideoId(this.videoUrl)
        if (!videoId) {
          console.log('Could not extract video ID from:', this.videoUrl)
          return ''
        }

        // Add timestamp for cache busting and enable autoplay
        const timestamp = new Date().getTime()
        const iframeSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&t=${timestamp}`
        console.log('Generated iframe src:', iframeSrc)
        return iframeSrc
      }
    },
    watch: {
      videoUrl(newUrl, oldUrl) {
        console.log('YoutubeFrame: videoUrl changed from:', oldUrl, 'to:', newUrl)
        if (newUrl) {
          const newVideoId = this.extractVideoId(newUrl)
          console.log('YoutubeFrame: extracted video ID:', newVideoId)
          
          // Only update if the video ID actually changed
          if (newVideoId !== this.currentVideoId) {
            this.currentVideoId = newVideoId
            if (this.player && window.YT) {
              console.log('Loading new video in existing player:', newVideoId)
              this.player.loadVideoById(newVideoId)
            } else {
              this.initPlayer()
            }
          }
        }
      }
    },
    mounted() {
      this.loadYouTubeAPI()
    },
    beforeDestroy() {
      if (this.player) {
        this.player.destroy()
      }
      if (this.checkInterval) {
        clearInterval(this.checkInterval)
      }
      if (this.advanceTimeout) {
        clearTimeout(this.advanceTimeout)
      }
    },
    methods: {
      extractVideoId(url) {
        if (!url) return null

        let id
        if (url.indexOf('youtu.be/') > -1) {
          id = url.split('/')[3]
        } else if (url.indexOf('youtube.com/watch') > -1) {
          id = url.split('?v=')[1]
          if (id && id.indexOf('&') > -1) {
            id = id.substring(0, id.indexOf('&'))
          }
        } else if (url.indexOf('youtube.com/embed/') > -1) {
          id = url.split('/embed/')[1]
          if (id && id.indexOf('?') > -1) {
            id = id.substring(0, id.indexOf('?'))
          }
        }
        return id
      },

      onIframeLoad() {
        console.log('YouTube iframe loaded for video:', this.currentVideoId)
        this.startDurationCheck()
      },

      onIframeError() {
        console.error('YouTube iframe failed to load')
        // If this was a playlist embed that failed, emit event to parent for fallback
        if (this.iframeSrc.includes('videoseries')) {
          console.log('Playlist embed failed, requesting fallback to individual video')
          this.$emit('playlistEmbedFailed')
        }
      },

      loadYouTubeAPI() {
        // Load YouTube IFrame API
        if (!window.YT) {
          const script = document.createElement('script')
          script.src = 'https://www.youtube.com/iframe_api'
          document.head.appendChild(script)
          
          window.onYouTubeIframeAPIReady = () => {
            this.initPlayer()
          }
        } else {
          this.initPlayer()
        }
      },

      initPlayer() {
        if (this.videoUrl) {
          const videoId = this.extractVideoId(this.videoUrl)
          if (videoId) {
            this.currentVideoId = videoId
            this.createPlayer()
          }
        }
      },

      createPlayer() {
        if (this.player) {
          this.player.destroy()
        }

        this.player = new window.YT.Player(this.$refs.youtubeContainer, {
          width: '100%',
          height: '100%',
          videoId: this.currentVideoId,
          playerVars: {
            autoplay: 1,
            rel: 0,
            modestbranding: 1
          },
          events: {
            onReady: this.onPlayerReady,
            onStateChange: this.onPlayerStateChange,
            onError: this.onPlayerError
          }
        })
      },

      onPlayerReady(event) {
        console.log('YouTube player ready')
      },

      onPlayerStateChange(event) {
        console.log('Player state changed:', event.data)
        // YT.PlayerState.ENDED = 0
        if (event.data === window.YT.PlayerState.ENDED) {
          console.log('Video ended, triggering auto-advance')
          this.$emit('videoEnded')
        }
      },

      onPlayerError(event) {
        console.error('YouTube player error:', event.data)
      },

      parseDuration(isoDuration) {
        const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
        if (!match) return 0

        const hours = parseInt(match[1] || '0', 10)
        const minutes = parseInt(match[2] || '0', 10)
        const seconds = parseInt(match[3] || '0', 10)

        return hours * 3600 + minutes * 60 + seconds
      }
    }
  }
</script>

<style scoped>
.iframe {
  width: 100%;
  height: 100%;
}
</style>
